import { EntityType } from '@frontend/common';
import { IdentificationClient } from '@frontend/identification/api';
import { Identification, IdentificationType } from '@frontend/identification/types';
import { Logger } from '@frontend/logger';
import { IUser } from '@frontend/user/types';
import { type Dispatch, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useScreen } from '../useScreen';

export interface LinkBadgeViewProps {
    badge: Identification | null;
    badgeLinked: boolean;
    selectedUser: IUser | null;
    isLinking: boolean;
    error: string | null;
    resetError: () => void;
    onSelectUser: Dispatch<IUser | null>;
    onConfirm: () => void;
    onBack: () => void;
}

export const useLinkBadge = (): LinkBadgeViewProps => {
    const intl = useIntl();
    const { onSubmit, onBack, wizardData, navigationState } = useScreen();
    const badge = (wizardData.badge as Identification) ?? null;
    const badgeKey = wizardData.badgeKey as string | null;
    const badgeLinked = badge?.entity_type === EntityType.USER && !!badge.entity_id;
    const currentUser = navigationState.user;
    const accountId = navigationState.iot?.account_id;
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [isLinking, setIsLinking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onSelectUser = useCallback((user: IUser | null) => {
        setSelectedUser(user);
        setError(null);
    }, []);

    const onConfirm = useCallback(async () => {
        if (!currentUser) return;
        if (!selectedUser || isLinking || badgeLinked) return;
        setIsLinking(true);
        setError(null);

        try {
            const existing = await IdentificationClient.fetchIdentifications({ entity_id: selectedUser.id, type: IdentificationType.BADGE });
            if (existing.results.length > 0) {
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.link-badge.user-already-has-badge',
                        defaultMessage: 'This user already has a badge linked.'
                    })
                );
                setIsLinking(false);
                return;
            }

            if (badge) {
                await IdentificationClient.patchIdentification(badge.account_id, badge.id, {
                    entity_id: selectedUser.id,
                    entity_type: EntityType.USER
                });
            } else if (badgeKey && accountId) {
                await IdentificationClient.postIdentification(accountId, {
                    key: badgeKey,
                    type: IdentificationType.BADGE,
                    entity_id: selectedUser.id,
                    entity_type: EntityType.USER
                });
            } else {
                setError(intl.formatMessage({ id: 'kiosk.wizard.link-badge.link-error', defaultMessage: 'Failed to link badge. Please try again.' }));
                setIsLinking(false);
                return;
            }

            onSubmit();
        } catch (e) {
            Logger.error('Failed to link badge to user', e);
            setError(intl.formatMessage({ id: 'kiosk.wizard.link-badge.link-error', defaultMessage: 'Failed to link badge. Please try again.' }));
        } finally {
            setIsLinking(false);
        }
    }, [accountId, badge, badgeKey, selectedUser, isLinking, badgeLinked, currentUser, onSubmit, intl]);

    return {
        badge,
        badgeLinked,
        selectedUser,
        isLinking,
        error,
        resetError: () => setError(null),
        onSelectUser,
        onConfirm,
        onBack
    };
};
